import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import {
  AnalyticsQueryTemplate,
  AnalyticsQueryTemplateStatus,
  QUERY_TYPE_TO_PATH_MAP,
  QUERY_TYPE_TO_SHORTNAME_MAP,
  QueryType,
} from 'dashboard/models/analytics-query-template';
import { loadQueryTemplate as loadInquiryQueryTemplate } from 'dashboard/redux/actions/metrics-inquiry';
import {
  loadChecksQueryTemplate,
  loadVolumeQueryTemplate,
} from 'dashboard/redux/slices/metrics-verification';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { assertNever } from 'lib/utils/types';

interface AnalyticsQueryTemplateTableProps {
  templates: AnalyticsQueryTemplate[];
  handleArchive: (id: string) => Promise<void>;
}

const StyledCard = styled(Card)`
  display: flex;
  padding: ${Spacing[4]} ${Spacing[5]};
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    cursor: pointer;
  }
`;

const AnalyticsQueryTemplateTable = (
  props: AnalyticsQueryTemplateTableConnectedProps & AnalyticsQueryTemplateTableProps,
) => {
  const {
    templates,
    loadChecksQueryTemplate,
    loadVolumeQueryTemplate,
    loadInquiryQueryTemplate,
    handleArchive,
  } = props;
  const navigate = useNavigate();
  const handleTemplateClick = (queryType: QueryType, template: AnalyticsQueryTemplate) => {
    switch (queryType) {
      case QueryType.VerificationsVolume:
        loadVolumeQueryTemplate({ queryTemplate: template });
        break;
      case QueryType.VerificationsChecks:
        loadChecksQueryTemplate({ queryTemplate: template });
        break;
      case QueryType.Inquiries:
        loadInquiryQueryTemplate(template);
        break;
      default:
        assertNever(queryType);
    }
    navigate(QUERY_TYPE_TO_PATH_MAP[queryType]);
  };

  return (
    <StyledCard>
      <Card.Title>Saved Queries</Card.Title>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>ID</TableHeaderCell>
            <TableHeaderCell>Type</TableHeaderCell>
            <TableHeaderCell sortable={true}>Last Updated</TableHeaderCell>
            <TableHeaderCell>Last Updated By</TableHeaderCell>
            {/* Empty Header/Column for the menu/button icon */}
            <TableHeaderCell></TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.map((template, i) => (
            <StyledTableRow
              id={`analytics-query-template-row-${i}-${template.attributes.name}`}
              key={`analytics-query-template-row-${i}-${template.attributes.name}`}
              onClick={(e) => {
                e.stopPropagation();
                template.attributes.status === AnalyticsQueryTemplateStatus.Active &&
                  handleTemplateClick(template.attributes.query.type, template);
              }}
            >
              <TableCell>{template.attributes.name}</TableCell>
              <TableCell>{template.id}</TableCell>
              <TableCell>
                {QUERY_TYPE_TO_SHORTNAME_MAP[template.attributes.query.type as QueryType]}
              </TableCell>
              <TableCell>
                {DateTime.fromISO(template.attributes['updated-at']).toFormat(DATE_AND_TIME_FORMAT)}
              </TableCell>
              <TableCell>{template.attributes['last-updater']}</TableCell>
              <TableCell>
                <DropdownMenu
                  side='bottom'
                  align='end'
                  trigger={
                    <Button width='icon' variant='basic' onClick={(e) => e.stopPropagation()}>
                      <MoreIcon />
                    </Button>
                  }
                >
                  <DropdownMenu.Item
                    id='metrics__query-template-archive-menu-item'
                    variant='danger'
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleArchive(template.id);
                    }}
                  >
                    Archive
                  </DropdownMenu.Item>
                </DropdownMenu>
              </TableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledCard>
  );
};

const mapDispatchToProps = {
  loadInquiryQueryTemplate,
  loadVolumeQueryTemplate,
  loadChecksQueryTemplate,
};

const connector = connect(null, mapDispatchToProps);
type AnalyticsQueryTemplateTableConnectedProps = ConnectedProps<typeof connector>;
export default connector(AnalyticsQueryTemplateTable);
